import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, Loader2, LogOut } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/stores/auth.store";
import { getMe, stopImpersonation } from "@/api/auth";

/** Pixel height of the impersonation banner. Sidebar/AppLayout offsets read from here. */
export const IMPERSONATION_BANNER_HEIGHT = 44;

/**
 * Persistent warning bar shown on top of the entire app whenever the
 * current session is an impersonation. Fixed top-0, z-50 so it sits
 * above everything else (including the sidebar). Cannot be dismissed —
 * the only way out is the "Switch back" button.
 */
export function ImpersonationBanner() {
  const user = useAuthStore((s) => s.user);
  const setTokens = useAuthStore((s) => s.setTokens);
  const setUser = useAuthStore((s) => s.setUser);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (!user?.impersonated_by) return null;

  async function handleSwitchBack() {
    setSubmitting(true);
    try {
      const tokens = await stopImpersonation();
      setTokens({
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token,
        token_type: tokens.token_type,
      });
      const me = await getMe();
      setUser(me);
      toast.success("Switched back to your account");
      navigate("/projects", { replace: true });
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 top-0 z-50 flex items-center justify-center gap-3 bg-amber-500 px-4 text-sm text-white shadow-md"
      style={{ height: IMPERSONATION_BANNER_HEIGHT }}
    >
      <Eye className="h-4 w-4 shrink-0" aria-hidden />
      <span className="truncate">
        Signed in as{" "}
        <span className="font-semibold">{user.full_name}</span>{" "}
        <span className="opacity-80">({user.email})</span>
      </span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleSwitchBack}
        disabled={submitting}
        className="h-7 border-white/60 bg-transparent text-white hover:bg-white/10 hover:text-white"
      >
        {submitting ? (
          <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
        ) : (
          <LogOut className="mr-2 h-3.5 w-3.5" />
        )}
        Switch back to my account
      </Button>
    </div>
  );
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.code === "ERR_NETWORK") return "Cannot reach the server.";
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "Couldn't switch back.";
}
